import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import {
  MOMENT_DATE_FORMAT_LONG,
  MOMENT_DATE_FORMAT_NORMAL,
  MOMENT_DATE_FORMAT_SHORT,
  MOMENT_DATE_FORMAT_WITH_DAY,
  MOMENT_DATE_LONG_NO_COMMA,
  MOMENT_DAY_FORMAT_LONG,
  MOMENT_DAY_FORMAT_NUMERIC,
  MOMENT_MONTH_FORMAT_SHORT,
  MOMENT_YEAR_FORMAT_LONG,
} from './DateFormats'
import { TIME_FORMAT } from './TimeFormat'

/**
 * Date formats which can be used with DateComponent and DatePipe.
 *
 * @see DateComponent
 * @see DatePipe
 */
export enum DateFormat {
  /**
   * @example "1:23 AM"
   */
  Time = 'time',
  /**
   * @example "01:23 AM"
   */
  TimeLeadingZero = 'timeLeadingZero',
  /**
   * @example "10:23:00 AM"
   */
  TimeWithSeconds = 'timeWithSeconds',
  /**
   * @example "10:23:00 AM"
   */
  TimeWithMilliseconds = 'timeWithMilliseconds',
  /**
   * @example "23:49:10"
   */
  TimeTwentyFourHourWithSeconds = 'timeTwentyFourHourWithSeconds',
  /**
   * @example "08:49"
   */
  TimeTwentyFourHourPadded = 'timeTwentyFourHourPadded',
  /**
   * @example "Oct 04, 2019"
   */
  Date = 'date',
  /**
   * @example "04 Oct 2019"
   */
  DateDayFirst = 'dateDayFirst',
  /**
   * @example "04 October, 2019"
   */
  DateLong = 'dateLong',
  /**
   * @example "04 October 2019"
   */
  DateLongNoComma = 'dateLongNoComma',
  /**
   * @example "04 October 2019, Thursday"
   */
  DateWithDay = 'dateWithDay',
  /**
   * @example "04 October, 2019 10:23 AM"
   */
  DateAndTime = 'dateAndTime',
  /**
   * @example "Oct 04, 2019 10:23 AM"
   */
  DateAndTimeShorter = 'dateAndTimeShorter',
  /**
   * @example "10/04/2019 10:23 AM"
   */
  DateAndTimeShort = 'dateAndTimeShort',
  /**
   * @example "Friday"
   */
  DayOfWeek = 'dayOfWeek',
}

export const ALL_DATE_FORMATS = Object.values(DateFormat) as DateFormat[]

/**
 * Given a date format, returns a moment formatter string which is can be used for
 * converting a date into the desired format.
 */
export function momentFormatter(desiredFormat: DateFormat): string {
  switch (desiredFormat) {
    case DateFormat.Time:
      return TIME_FORMAT.TWELVE_HOUR.MINUTE_PRECISION.DEFAULT
    case DateFormat.TimeLeadingZero:
      return TIME_FORMAT.TWELVE_HOUR.MINUTE_PRECISION.LEADING_ZERO
    case DateFormat.TimeWithSeconds:
      return TIME_FORMAT.TWELVE_HOUR.SECOND_PRECISION.DEFAULT
    case DateFormat.TimeWithMilliseconds:
      return TIME_FORMAT.TWELVE_HOUR.MILLISECOND_PRECISION.DEFAULT
    case DateFormat.TimeTwentyFourHourWithSeconds:
      return TIME_FORMAT.TWENTY_FOUR_HOUR.SECOND_PRECISION.DEFAULT
    case DateFormat.TimeTwentyFourHourPadded:
      return TIME_FORMAT.TWENTY_FOUR_HOUR.MINUTE_PRECISION.PADDED
    case DateFormat.Date:
      return MOMENT_DATE_FORMAT_NORMAL
    case DateFormat.DateDayFirst:
      return `${MOMENT_DAY_FORMAT_NUMERIC} ${MOMENT_MONTH_FORMAT_SHORT} ${MOMENT_YEAR_FORMAT_LONG}`
    case DateFormat.DateLong:
      return MOMENT_DATE_FORMAT_LONG
    case DateFormat.DateLongNoComma:
      return MOMENT_DATE_LONG_NO_COMMA
    case DateFormat.DateWithDay:
      return MOMENT_DATE_FORMAT_WITH_DAY
    case DateFormat.DateAndTime:
      return `${MOMENT_DATE_FORMAT_LONG} ${TIME_FORMAT.TWELVE_HOUR.MINUTE_PRECISION.DEFAULT}`
    case DateFormat.DateAndTimeShorter:
      return `${MOMENT_DATE_FORMAT_NORMAL} ${TIME_FORMAT.TWELVE_HOUR.MINUTE_PRECISION.DEFAULT}`
    case DateFormat.DateAndTimeShort:
      return `${MOMENT_DATE_FORMAT_SHORT} ${TIME_FORMAT.TWELVE_HOUR.MINUTE_PRECISION.DEFAULT}`
    case DateFormat.DayOfWeek:
      return MOMENT_DAY_FORMAT_LONG
    default:
      return assertUnreachable(desiredFormat)
  }
}
